import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
    } catch (err) {
      console.log('Contact form submitted (no backend endpoint yet)');
    }
    setSubmitted(true);
    setSubmitting(false);
    setTimeout(() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#FEFCF9]">
      {/* Hero */}
      <section className="py-24 bg-[#F5EFE6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs tracking-[0.3em] text-[#C4A265] uppercase font-medium mb-4">Reach Out</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-[#3E2723] mb-6">Get In Touch</h1>
            <div className="w-12 h-px bg-[#C4A265] mx-auto mb-6"></div>
            <p className="text-lg text-[#7A6B5D] leading-relaxed">
              We'd love to hear from you. Whether you have questions about our products or want to discuss custom orders.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Info */}
            <div>
              <h2 className="text-3xl font-serif text-[#3E2723] mb-2">Contact Information</h2>
              <div className="w-12 h-px bg-[#C4A265] mb-8"></div>
              <div className="space-y-6 mb-12">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#F5EFE6] border border-[#E8DCC8] rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#C4A265]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#3E2723] mb-1">Email</h3>
                    <a href="mailto:behat.house@gmail.com" className="text-[#7A6B5D] hover:text-[#C4A265] transition-colors text-sm">behat.house@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#F5EFE6] border border-[#E8DCC8] rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#C4A265]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#3E2723] mb-1">WhatsApp / Phone</h3>
                    <a href="https://wa.me/918700465253" target="_blank" rel="noopener noreferrer" className="text-[#7A6B5D] hover:text-[#C4A265] transition-colors text-sm">+91 87004 65253</a>
                    <p className="text-xs text-[#C4A265] mt-1">Mon-Sat: 10:00 AM - 6:00 PM IST</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-11 h-11 bg-[#F5EFE6] border border-[#E8DCC8] rounded-full flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#C4A265]" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#3E2723] mb-1">Address</h3>
                    <p className="text-[#7A6B5D] text-sm">
                      SP/34, Shankarpuri Colony,<br />
                      Behat Road, Saharanpur,<br />
                      Uttar Pradesh 247001
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-serif text-[#3E2723] mb-4">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/behathouse" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-[#3E2723] hover:bg-[#C4A265] rounded-full flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-[#FEFCF9]" />
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-8">
              <h2 className="text-3xl font-serif text-[#3E2723] mb-2">Send Us a Message</h2>
              <div className="w-12 h-px bg-[#C4A265] mb-6"></div>
              {submitted ? (
                <div className="bg-[#F5EFE6] border border-[#E8DCC8] p-6 text-center">
                  <p className="text-[#3E2723] font-semibold mb-2">Thank you for contacting us!</p>
                  <p className="text-[#7A6B5D]">We'll get back to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {[
                    { label: 'Name', name: 'name', type: 'text' },
                    { label: 'Email', name: 'email', type: 'email' },
                    { label: 'Subject', name: 'subject', type: 'text' }
                  ].map(f => (
                    <div key={f.name}>
                      <label className="block text-sm font-medium text-[#3E2723] mb-1 tracking-wider uppercase">{f.label} *</label>
                      <input type={f.type} name={f.name} value={formData[f.name]} onChange={handleInputChange} required
                        className="w-full px-4 py-3 border border-[#E8DCC8] bg-white focus:border-[#C4A265] focus:outline-none transition-colors"
                        data-testid={`contact-${f.name}-input`} />
                    </div>
                  ))}
                  <div>
                    <label className="block text-sm font-medium text-[#3E2723] mb-1 tracking-wider uppercase">Message *</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} required rows={6}
                      className="w-full px-4 py-3 border border-[#E8DCC8] bg-white focus:border-[#C4A265] focus:outline-none transition-colors"
                      data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={submitting}
                    className="w-full bg-[#3E2723] text-[#FEFCF9] py-3.5 font-semibold hover:bg-[#C4A265] transition-colors tracking-wider uppercase text-sm disabled:opacity-70"
                    data-testid="contact-submit-button">
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
